import { ComponentEntry, PropDef } from './types';

/**
 * BoundValue-typed props whose adapter treats the resolved value as an array (`.map`/`.length`) —
 * the catalog's `BoundValue` type string can't express this, so it's disambiguated by name. Shared
 * by every consumer that needs a minimal-but-valid instance of a catalog entry: the design-system
 * L5 render probe (`buildRenderProbe.ts`) and space-format's per-catalog-entry corpus fixtures
 * (`corpus/catalogEntryFixtures.ts`, SC10's catalog-entry coverage axis).
 */
const ARRAY_VALUED_BOUND_PROPS = new Set(['items', 'options', 'schemas']);

function generatePropValue(name: string, prop: PropDef): unknown {
  if (name === 'gridPosition') return { x: 0, y: 0, width: 1, height: 1 };
  if (name === 'unitConfig') return { category: 'length', defaultUnit: 'm' };
  if (name === 'template' && prop.type === 'ComponentEntry') {
    return { id: 'minimal-required-props-template', component: 'Text', content: 'probe' };
  }
  if (prop.type === 'BoundValue' && ARRAY_VALUED_BOUND_PROPS.has(name)) return [];
  if (prop.enum && prop.enum.length > 0) return prop.enum[0];

  switch (prop.type) {
    case 'string':
      return 'minimal-required-props-value';
    case 'string[]':
      return [];
    case 'boolean':
      return false;
    case 'number':
      return 0;
    case 'BoundValue':
      return 'minimal-required-props-value';
    default:
      // Object/array-literal shapes (e.g. `Array<{ label: BoundValue, children: string[] }>`,
      // `{ collapsible?: boolean; draggable?: boolean }`) — an empty container is a legal value
      // for every such shape in the catalog today (all of their own members are optional).
      return prop.type.startsWith('Array<') ? [] : {};
  }
}

/** Assigns `value` at a dotted path (e.g. `scorecardSettings.viewSystemName` — the catalog's convention for documenting one required member of an otherwise-optional nested config prop) onto `target`, creating intermediate objects as needed. */
function assignAtPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.');
  const leaf = segments.pop() as string;
  let cursor = target;
  for (const segment of segments) {
    const next = cursor[segment];
    cursor = (typeof next === 'object' && next !== null ? next : (cursor[segment] = {})) as Record<string, unknown>;
  }
  cursor[leaf] = value;
}

/**
 * Builds a minimal-but-valid props record for `entry` with every REQUIRED prop filled — optional
 * props are omitted so the caller exercises its own defaults. Best-effort: a prop whose real
 * runtime contract is stricter than this generator assumes is real signal for the caller (a render
 * throw, a schema-check failure), not a fixture bug, unless investigation shows otherwise.
 */
export function buildMinimalRequiredProps(entry: ComponentEntry): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const [name, def] of Object.entries(entry.props)) {
    if (!def.required) continue;
    assignAtPath(props, name, generatePropValue(name.split('.').pop() as string, def));
  }
  return props;
}
